<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  type PropType,
  ref,
  watch,
} from "vue";
import {
  ControlSize,
  DesignVersion,
  DropdownType,
  OptionItems,
  Placement,
  PromiseLike,
} from "@/models/types";
import BcMenu from "./bc-menu.vue";
import { BcInput } from "..";
import { MenuEntry } from "../../models";

defineOptions({ name: "BcDropdown" });

/** Specifies two-way binding for the dropdown */
const modelValue = defineModel<any>({ default: undefined });

const props = defineProps({
  /** Allows to write into the dropdown's input field */
  allowInput: { type: Boolean, default: true },
  /** Allows null value for the dropdown */
  allowNull: { type: Boolean, default: false },
  /** Allows items to overflow the container element */
  autoWidth: { type: Boolean, default: false },
  /** Defines the dropdown's style */
  design: {
    type: String as PropType<DesignVersion>,
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Specifies custom check for disabled items */
  checkDisabled: { type: Function as PropType<(item: any) => boolean> },
  /** Specifies the timeout to wait before close the dropdown */
  closeTimeout: {
    type: Number,
    default: 100,
    validator: (value: any) => typeof value === "number" && value >= 0,
  },
  /** Disables items for selection */
  disabled: { type: Boolean, default: false },
  /** Specifies custom validation messages */
  errors: {
    type: Map as PropType<Map<string, string>>,
    default: () => new Map<string, string>(),
  },
  textWrap: { type: Boolean, default: false },
  /** Expands the dropdown to the width of its parent container */
  fullWidth: { type: Boolean, default: false },
  /** Specifies custom calculation for item key */
  getKey: { type: Function as PropType<(item: any) => string> },
  /** Specifies custom calculation for item text */
  getText: { type: Function as PropType<(item: any) => string> },
  /** Specifies the disabled property (e.g. disabled) for disabel item selection if items are objects */
  itemDisabledProperty: { type: String },
  /** Sets a divider between items */
  itemDivider: { type: Boolean, default: true },
  /** Specifies the key property (e.g. ID) for identification if items are objects */
  itemKeyProperty: { type: String },
  /** Specifies the text property (e.g. name) for display if items are objects */
  itemTextProperty: { type: String },
  /** Specifies the selectable items */
  items: {
    type: [Array, Function, Promise] as PropType<OptionItems>,
    required: true,
    validator: (value: any) =>
      Array.isArray(value) ||
      typeof value === "function" ||
      PromiseLike.isPromiseLike(value),
  },
  /** Specifies the number of visible items */
  numberOfVisibleItems: { type: Number },
  /** Specifies a short hint for the select */
  placeholder: { type: [String, Number, Boolean], default: undefined },
  /** Specifies the placement of the dropdown items compared to the container element */
  placement: {
    type: String as PropType<Placement>,
    default: "bottom-start",
    validator: (value: any) => Placement.isPlacement(value),
  },
  /** Specifies that dropdown's offset relative to the selected element container */
  offset: { type: Number },
  /** Specifies that dropdown is read-only */
  readonly: { type: Boolean, default: false },
  /** Specifies if selecting an item is required */
  required: { type: Boolean, default: false },
  /** Specifies pre-defined sizes for the dropdown */
  size: {
    type: String as PropType<ControlSize>,
    validator: (value: any) => ControlSize.isControlSize(value),
  },
  /** Defines the dropdown's type */
  type: {
    type: String as PropType<DropdownType>,
    default: "dropdown",
    validator: (value: any) => DropdownType.isDropdownType(value),
  },
});

const emit = defineEmits<{
  (e: "change", payload: any): void;
  (e: "update:model-value", payload: any): void;
}>();

let resolvedItems = ref(new Array<any>());
const validity = ref(true);
const showItems = ref(false);
const errorKeys = ref<string[]>([]);

const hideLastCall = ref<any>(null);
const sameWidth = ref(false);
const prevValue = ref<string | number | null | undefined>("");

const menuRef = ref<InstanceType<typeof BcMenu>>();
const bcInputRef = ref<InstanceType<typeof BcInput>>();
const dropdownControlRef = ref<HTMLDivElement>();
const selectedItemContainerRef = ref<HTMLDivElement>();

const pak = computed<string>(() => {
  return "bc-controls-d" + (props.design == "6" ? "6" : "7");
});

const inputValue = computed(() => {
  return selectedItem.value === undefined
    ? undefined
    : calculateText(selectedItem.value) ?? bcInputRef.value?.computedValue;
});

const selectedIdx = computed<number>(() => {
  if (typeof modelValue.value !== "undefined") {
    return resolvedItems.value.findIndex((item) =>
      calculateKey(item) !== undefined && calculateKey(modelValue.value) !== undefined
        ? calculateKey(item) === calculateKey(modelValue.value)
        : calculateText(item) === calculateText(modelValue.value)
    );
  }
  return resolvedItems.value && bcInputRef.value
    ? resolvedItems.value.findIndex(
        (item) => calculateText(item) === bcInputRef.value!.computedValue
      )
    : -1;
});

const selectedItem = computed<any>(() => {
  if (typeof modelValue.value !== "undefined") {
    return resolvedItems.value.find((item) =>
      calculateKey(item) !== undefined && calculateKey(modelValue.value) !== undefined
        ? calculateKey(item) === calculateKey(modelValue.value)
        : calculateText(item) === calculateText(modelValue.value)
    );
  }
  return resolvedItems.value && bcInputRef.value
    ? resolvedItems.value.find(
        (item) => calculateText(item) === bcInputRef.value!.computedValue
      )
    : undefined;
});

const items = computed(() => {
  return MenuEntry.getMenuEntries(
    resolvedItems.value,
    props.itemTextProperty,
    props.getText,
    props.itemKeyProperty,
    props.getKey,
    props.itemDisabledProperty,
    props.checkDisabled
  );
});

const selectedItemEntry = computed<MenuEntry | undefined>(() => {
  if (!selectedItem.value) return;
  const sItem = selectedItem.value;
  const item = new MenuEntry(calculateText(sItem), calculateKey(sItem));
  item.disabled = getDisabled(sItem);
  item.icon = sItem.icon;
  return item;
});

onMounted(() => {
  nextTick(() => {
    if (props.type === "select") {
      document.addEventListener("click", handleClickEvent);
      document.addEventListener("keydown", handleKeydownEvent);
    }
    onModelChanged();
    prevValue.value = inputValue.value;
  });
});

onUnmounted(() => {
  if (props.type === "select") {
    document.removeEventListener("click", handleClickEvent);
    document.removeEventListener("keydown", handleKeydownEvent);
  }
});

const show = async function (): Promise<void> {
  if (props.disabled || props.readonly || showItems.value) {
    return;
  }

  if (hideLastCall.value) {
    clearTimeout(hideLastCall.value ?? undefined);
  }

  try {
    resolvedItems.value = await OptionItems.resolveItems(props.items);
    if (resolvedItems.value.length) {
      onModelChanged();
    }
  } catch (error: any) {
    if (error?.message !== "canceled") {
      console.error(error);
    }
  }

  showItems.value = true;
};

const hide = function (): void {
  if (hideLastCall.value) {
    clearTimeout(hideLastCall.value ?? undefined);
  }
  hideLastCall.value = setTimeout(() => {
    showItems.value = false;
    bcInputRef.value?.inputRef?.blur();
    dropdownControlRef.value?.blur();
  }, props.closeTimeout);
};

function getDisabled(item: any): boolean {
  if (props.checkDisabled) {
    const disabled = props.checkDisabled(item);
    if (typeof disabled === "boolean") {
      return disabled;
    } else {
      console.warn("Custom disabled check function is invalid.");
    }
  } else if (
    typeof item === "object" &&
    item !== null &&
    props.itemDisabledProperty &&
    props.itemDisabledProperty in item &&
    typeof item[props.itemDisabledProperty] === "boolean"
  ) {
    return item[props.itemDisabledProperty];
  }
  return false;
}

const getValidationMessage = function (errorKey: string): string {
  return props.errors.get(errorKey) ?? errorKey;
};

const checkValidity = function (): boolean {
  errorKeys.value = [];

  if (props.disabled || props.readonly || !bcInputRef.value) {
    validity.value = true;
    return validity.value;
  }

  if (
    props.required &&
    props.type === "dropdown" &&
    bcInputRef.value &&
    (bcInputRef.value?.computedValue === undefined ||
      bcInputRef.value?.computedValue === null ||
      bcInputRef.value?.computedValue === "")
  ) {
    setError("valueMissing", "Value missing");
  } else if (
    getDisabled(
      resolvedItems.value.find(
        (item) => calculateText(item) === bcInputRef.value?.computedValue
      )
    )
  ) {
    setError("disabledItem", "Disabled item");
  } else if (
    resolvedItems.value.every(
      (item) => calculateText(item) !== bcInputRef.value?.computedValue
    )
  ) {
    setError("invalidItem", "Invalid item");
  } else {
    validity.value = true;
  }

  menuRef.value?.update();
  return validity.value;
};

async function selectItem(item: any): Promise<void> {
  if (getDisabled(item)) return;

  if (resolvedItems.value.find((resolvedItem) => resolvedItem === item)) {
    if (modelValue?.value !== item) {
      modelValue.value = item;
      emit("change", item);
    }
    await nextTick();
    prevValue.value = bcInputRef.value?.computedValue;
    await nextTick();
    checkValidity();
  }
}

async function onMenuClick(entry: MenuEntry): Promise<void> {
  const item = resolvedItems.value.find((resolvedItem) =>
    calculateKey(resolvedItem) !== undefined
      ? calculateKey(resolvedItem) === entry.key
      : calculateText(resolvedItem) === entry.text
  );

  if (item) {
    selectItem(item);
    hide();
  }
}

const moveSelection = async function (direction: number): Promise<void> {
  if (props.disabled || props.readonly) return;

  let newSelectedIdx = selectedIdx.value + direction;
  while (
    newSelectedIdx > 0 &&
    newSelectedIdx < resolvedItems.value.length - 1 &&
    getDisabled(resolvedItems.value[newSelectedIdx])
  ) {
    newSelectedIdx += direction;
  }

  selectItem(resolvedItems.value[newSelectedIdx]);
};

function calculateKey(item: any): any {
  if (props.getKey) {
    return props.getKey(item);
  }
  if (
    typeof item === "object" &&
    item !== null &&
    props.itemKeyProperty &&
    props.itemKeyProperty in item
  ) {
    return item[props.itemKeyProperty]?.toString() ?? "";
  }
  return undefined;
}

function calculateText(item: any): string {
  if (props.getText) {
    return props.getText(item);
  }
  if (
    typeof item === "object" &&
    item !== null &&
    props.itemTextProperty &&
    props.itemTextProperty in item
  ) {
    return item[props.itemTextProperty]?.toString() ?? "";
  }
  return item?.toString() ?? "";
}

/** Checks item properties */
function checkItemProperties(): void {
  if (
    props.itemKeyProperty &&
    resolvedItems.value?.some(
      (resolvedItem) =>
        props.itemKeyProperty &&
        (typeof resolvedItem !== "object" ||
          (typeof resolvedItem === "object" &&
            resolvedItem !== null &&
            !(props.itemKeyProperty in resolvedItem)))
    )
  ) {
    console.error(
      `Item key property '${props.itemKeyProperty}' is not available for all items.`
    );
  }
  if (
    props.itemKeyProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(props.itemKeyProperty in modelValue.value)))
  ) {
    console.error(
      `Item key property '${props.itemKeyProperty}' is not available in the model.`
    );
  }

  if (
    !props.itemTextProperty &&
    !props.getText &&
    resolvedItems.value?.some(
      (resolvedItem) => typeof resolvedItem === "object" && resolvedItem !== null
    )
  ) {
    console.error("Item text property is not defined for object items.");
  }
  if (
    !props.itemTextProperty &&
    !props.getText &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null
  ) {
    console.error("Item text property is not defined for model.");
  }
  if (
    props.itemTextProperty &&
    resolvedItems.value?.some(
      (resolvedItem) =>
        props.itemTextProperty &&
        (typeof resolvedItem !== "object" ||
          (typeof resolvedItem === "object" &&
            resolvedItem !== null &&
            !(props.itemTextProperty in resolvedItem)))
    )
  ) {
    console.error(
      `Item text property '${props.itemTextProperty}' is not available for all items.`
    );
  }
  if (
    props.itemTextProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(props.itemTextProperty in modelValue.value)))
  ) {
    console.error(
      `Item text property '${props.itemTextProperty}' is not available in the model.`
    );
  }

  if (
    props.itemDisabledProperty &&
    resolvedItems.value?.some(
      (resolvedItem) =>
        props.itemDisabledProperty &&
        typeof resolvedItem === "object" &&
        resolvedItem !== null &&
        props.itemDisabledProperty in resolvedItem &&
        resolvedItem[props.itemDisabledProperty] !== null &&
        typeof resolvedItem[props.itemDisabledProperty] !== "boolean"
    )
  ) {
    console.error("Disabled value is not boolean in one of the items.");
  }
  if (
    props.itemDisabledProperty &&
    modelValue.value &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null &&
    props.itemDisabledProperty in modelValue.value &&
    modelValue.value[props.itemDisabledProperty as keyof Object] !== null &&
    typeof modelValue.value[props.itemDisabledProperty as keyof Object] !== "boolean"
  ) {
    console.error("Disabled value is not boolean in model.");
  }
}

function handleClickEvent(event: MouseEvent): void {
  if (
    props.type === "select" &&
    dropdownControlRef.value &&
    event.target !== dropdownControlRef.value &&
    !dropdownControlRef.value.contains(event.target as Node) &&
    showItems.value
  ) {
    hide();
  }
}

function handleKeydownEvent(event: KeyboardEvent): void {
  if ((event.key === "Escape" || event.key === "Enter") && showItems.value) {
    hide();
  }
}

function onDisabledOrReadonlyChanged(value: boolean): void {
  // Hide item list and validation message
  if (value) {
    hide();
  }
}

function onErrorsChanged(): void {
  nextTick(async () => {
    if (!props.disabled && !props.readonly) {
      await nextTick();
      checkValidity();
    }
  });
}

async function onItemsChanged() {
  resolvedItems.value = await OptionItems.resolveItems(props.items);
  onModelChanged();
}

function onModelChanged(): void {
  nextTick(() => {
    if (typeof modelValue.value !== "undefined") {
      if (!validity.value) {
        checkValidity();
      }
      checkItemProperties();
    }
  });
}

function setError(messageKey: string, messageText: string) {
  errorKeys.value.push(messageKey);
  if (!props.errors.has(messageKey)) {
    props.errors.set(messageKey, messageText);
  }
  if (bcInputRef.value?.inputRef)
    bcInputRef.value.inputRef.title = getValidationMessage(messageText);
  validity.value = false;
}

async function onInputTextChange(inputText: string | number | null | undefined) {
  if (props.disabled || props.readonly) return;

  try {
    modelValue.value = undefined;
    resolvedItems.value = (await OptionItems.resolveItems(props.items)).filter((item) =>
      calculateText(item)
        ?.toUpperCase()
        .includes(inputText?.toString()?.toUpperCase() ?? "")
    );
  } catch (error: any) {
    if (error?.message !== "canceled") {
      console.error(error);
    }
  }
}

async function setInputItem() {
  if (props.disabled || props.readonly || !props.allowInput) return;

  await setTimeout(async () => {
    if (!bcInputRef.value?.computedValue || bcInputRef.value.computedValue == "") return;

    const selectedInputItem = resolvedItems.value.find(
      (item) => calculateText(item) === bcInputRef.value!.computedValue
    );

    if (selectedInputItem && !getDisabled(selectedInputItem)) {
      modelValue.value = selectedInputItem;
      emit("change", selectedInputItem);
      hide();
      await nextTick();
      prevValue.value = bcInputRef.value?.computedValue;
    }
  }, 0);
  await nextTick();
  checkValidity();
}

function onFocus() {
  if (!showItems && !props.disabled && props.allowInput && bcInputRef.value?.inputRef) {
    bcInputRef.value.inputRef.focus();
    show();
  }
}

async function onEscape() {
  if (!bcInputRef.value) return;

  if (props.allowInput) {
    bcInputRef.value.computedValue = prevValue.value;
  }
  await nextTick();
  hideLastCall.value = setTimeout(async () => {
    resolvedItems.value = await OptionItems.resolveItems(props.items);
    hide();
  }, props.closeTimeout);
}

watch(() => props.items, onItemsChanged, { deep: true, immediate: true });
watch(modelValue, onModelChanged, { deep: true });
watch(() => props.disabled, onDisabledOrReadonlyChanged);
watch(() => props.readonly, onDisabledOrReadonlyChanged);
watch(() => props.errors, onErrorsChanged, { deep: true });

defineExpose({
  modelValue,
  resolvedItems,
  selectedIdx,
  validity,
  showItems,
  errorKeys,
  sameWidth,
  bcInputRef,
  dropdownControlRef,
  inputValue,
  selectedItem,
  show,
  hide,
  getValidationMessage,
  checkValidity,
  selectItem,
  moveSelection,
});
</script>
<template>
  <div
    ref="dropdownControlRef"
    :data-pak="pak"
    class="bissantzControls dropdownControl"
    :class="[
      size ? `dropdown-${size}` : '',
      {
        fullWidth: fullWidth,
        disabled: disabled,
        readonly: readonly,
      },
    ]"
    :tabindex="!disabled ? 0 : -1"
    :data-design="design"
    :data-type="type"
    @focus="onFocus()"
    @blur="hide()"
    @keydown.esc.stop.prevent="onEscape()"
    @keydown.up.stop.prevent="moveSelection(-1)"
    @keydown.down.stop.prevent="moveSelection(1)"
    @mouseleave="type === 'dropdown' ? hide() : null"
    @mouseenter="type === 'dropdown' ? show() : null"
  >
    <div
      ref="selectedItemContainerRef"
      class="selectedItemContainer"
      :class="{ disabled: disabled, readonly: readonly, invalid: !validity }"
    >
      <template v-if="type === 'dropdown'">
        <slot
          name="before-item"
          :item="selectedItem"
          :entry="selectedIdx !== -1 ? items[selectedIdx] : undefined"
          :itemIdx="selectedIdx"
          :itemKey="
            selectedItem !== undefined && calculateKey(selectedItem) !== undefined
              ? calculateKey(selectedItem)
              : -1
          "
          :itemText="selectedItem !== undefined ? calculateText(selectedItem) : ''"
        >
          <component v-if="selectedItem?.icon" :is="selectedItem.icon" />
        </slot>
        <bc-input
          ref="bcInputRef"
          @focus="bcInputRef ? (prevValue = bcInputRef.computedValue) : undefined"
          @blur="setInputItem"
          @keydown.enter.stop.prevent="setInputItem"
          @input="onInputTextChange"
          @click.stop.prevent="show"
          fullWidth
          :modelValue="inputValue"
          class="dropdownText"
          :class="{ invalid: !validity }"
          :design="design"
          :errors="errors"
          :readonly="!allowInput || readonly || disabled"
          :size="size"
          :placeholder="placeholder"
          tabindex="-1"
        />
      </template>
      <template v-if="type === 'select'">
        <div class="selectContainer" @click="showItems ? hide() : show()">
          <div class="selectedItem" :tabindex="!disabled ? 0 : -1">
            <slot
              name="before-item"
              :item="selectedItem"
              :entry="selectedIdx !== -1 ? items[selectedIdx] : undefined"
              :itemIdx="selectedIdx"
              :itemKey="
                selectedItem !== undefined && calculateKey(selectedItem) !== undefined
                  ? calculateKey(selectedItem)
                  : -1
              "
              :itemText="selectedItem !== undefined ? calculateText(selectedItem) : ''"
            >
              <component v-if="selectedItem?.icon" :is="selectedItem.icon" />
            </slot>
            <span>
              {{ selectedItem === undefined ? placeholder : inputValue }}
            </span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="30"
            viewBox="0 -960 960 960"
            width="30"
          >
            <path
              d="M480-357q-6 0-11-2t-10-7L261-564q-9-9-9-21t9-21q9-9 21.5-9t21.5 9l176 176 176-176q9-9 21-9t21 9q9 9 9 21.5t-9 21.5L501-366q-5 5-10 7t-11 2Z"
            />
          </svg>
        </div>
      </template>
    </div>
    <div
      class="validationMessage"
      v-if="!disabled && !readonly && !validity && errorKeys.length"
    >
      <span v-for="(errorKey, idx) in errorKeys" :key="idx">
        {{ getValidationMessage(errorKey) }}
      </span>
    </div>
    <bc-menu
      ref="menuRef"
      :design="design"
      :numberOfVisibleItems="numberOfVisibleItems"
      :separateAll="true"
      :isVisible="showItems"
      :items="items"
      :offset="offset ?? type === 'dropdown' ? -1 : 0"
      :placement="placement"
      :fullWidth="true"
      :textWrap="textWrap"
      :parentRect="selectedItemContainerRef"
      :selected-item="selectedItemEntry"
      @click="onMenuClick"
    >
      <template v-slot:before-item="{ entryIdx, entry }">
        <slot
          name="before-item"
          :item="resolvedItems[entryIdx]"
          :entry="entry"
          :itemIdx="entryIdx"
          :itemKey="
            calculateKey(resolvedItems[entryIdx]) !== undefined
              ? calculateKey(resolvedItems[entryIdx])
              : entryIdx
          "
          :itemText="calculateText(resolvedItems[entryIdx])"
        />
      </template>
    </bc-menu>
  </div>
</template>

<style lang="less">
@import "@/styles/components/menu/bc-dropdown.less";
</style>
